import { nodeOwnsSyncs } from "../sync-owning-nodes";
import type { JourneyContextVariableType } from "../types/config-types";
import {
  JourneyNodeSyncMode,
  JourneyNodeType,
} from "../types/journey-node-types";
import { JOURNEY_ENVELOPE_ERROR_CODES } from "./error-codes";
import type {
  JourneyEnvelopeErrorCode,
  JourneyEnvelopeIssueParams,
} from "./error-codes";
import {
  journeyContextConfigSchema,
  journeyEnvelopeNodeSchema,
  journeyNoteSchema,
} from "./schemas";
import type {
  JourneyEnvelopeNode,
  SegmentBranchConfig,
  TolerantJourneyGraphEnvelope,
} from "./schemas";
type JourneyEnvelopeIssueContext = {
  addIssue: (issue: {
    code: "custom";
    message: string;
    params: JourneyEnvelopeIssueParams;
    path: Array<string | number>;
  }) => void;
};

const addIssue = (
  context: JourneyEnvelopeIssueContext,
  code: JourneyEnvelopeErrorCode,
  message: string,
  path: Array<string | number>,
) => {
  context.addIssue({
    code: "custom",
    message,
    params: { code, bucket: "violations" },
    path,
  });
};

export const hasCycle = (
  nodeIds: Set<string>,
  edges: Array<{ from_node_id: string; to_node_id: string }>,
) => {
  const adjacency = new Map<string, string[]>();
  const inDegree = new Map([...nodeIds].map((nodeId) => [nodeId, 0]));
  for (const edge of edges) {
    if (!nodeIds.has(edge.from_node_id) || !nodeIds.has(edge.to_node_id)) {
      continue;
    }
    adjacency.set(edge.from_node_id, [
      ...(adjacency.get(edge.from_node_id) ?? []),
      edge.to_node_id,
    ]);
    inDegree.set(edge.to_node_id, (inDegree.get(edge.to_node_id) ?? 0) + 1);
  }

  const ready = [...inDegree]
    .filter(([, degree]) => degree === 0)
    .map(([nodeId]) => nodeId);
  let visited = 0;
  while (ready.length > 0) {
    const nodeId = ready.pop();
    if (nodeId === undefined) {
      break;
    }
    visited += 1;
    for (const childId of adjacency.get(nodeId) ?? []) {
      const nextDegree = (inDegree.get(childId) ?? 0) - 1;
      inDegree.set(childId, nextDegree);
      if (nextDegree === 0) {
        ready.push(childId);
      }
    }
  }
  return visited !== nodeIds.size;
};

const getNodeId = (node: unknown): string | undefined => {
  if (
    typeof node !== "object" ||
    node === null ||
    !("id" in node) ||
    typeof node.id !== "string"
  ) {
    return undefined;
  }
  return node.id;
};

const getEdge = (
  edge: unknown,
): { from_node_id: string; to_node_id: string } | undefined => {
  if (
    typeof edge !== "object" ||
    edge === null ||
    !("from_node_id" in edge) ||
    typeof edge.from_node_id !== "string" ||
    !("to_node_id" in edge) ||
    typeof edge.to_node_id !== "string"
  ) {
    return undefined;
  }
  return {
    from_node_id: edge.from_node_id,
    to_node_id: edge.to_node_id,
  };
};

/**
 * Node types allowed more than one child, mapped to the branch type those
 * children must be, or null when they are ordinary nodes. The save boundary
 * enforces the same rule, so it reads this rather than keeping its own list.
 */
export const FAN_OUT_NODE_TYPES = new Map<
  JourneyNodeType,
  JourneyNodeType | null
>([
  [JourneyNodeType.Segments, JourneyNodeType.SegmentBranch],
  [JourneyNodeType.Splits, JourneyNodeType.SplitBranch],
  [JourneyNodeType.WaitUntilEvent, JourneyNodeType.WaitUntilEventBranch],
  [JourneyNodeType.Decision, null],
  // A Function node branches with segment branches, so it has no branch type here. The core validator checks it.
  [JourneyNodeType.Function, null],
]);

export const validateJourneyGraphEnvelopeTopologyRules = (
  envelope: Pick<TolerantJourneyGraphEnvelope, "nodes" | "edges">,
  context: JourneyEnvelopeIssueContext,
) => {
  const nodeIds = new Set<string>();
  for (const [index, node] of envelope.nodes.entries()) {
    const nodeId = getNodeId(node);
    if (nodeId === undefined) {
      continue;
    }
    if (nodeIds.has(nodeId)) {
      addIssue(
        context,
        JOURNEY_ENVELOPE_ERROR_CODES.DuplicateNodeId,
        `Node ID ${nodeId} is duplicated`,
        ["nodes", index, "id"],
      );
    }
    nodeIds.add(nodeId);
  }

  const edges: Array<{ from_node_id: string; to_node_id: string }> = [];
  const edgeTargetsBySource = new Map<string, Set<string>>();
  for (const [index, rawEdge] of envelope.edges.entries()) {
    const edge = getEdge(rawEdge);
    if (edge === undefined) {
      continue;
    }
    edges.push(edge);
    const seenTargets = edgeTargetsBySource.get(edge.from_node_id) ?? new Set();
    if (seenTargets.has(edge.to_node_id)) {
      addIssue(
        context,
        JOURNEY_ENVELOPE_ERROR_CODES.DuplicateEdge,
        `Edge ${edge.from_node_id} -> ${edge.to_node_id} is duplicated`,
        ["edges", index],
      );
    }
    seenTargets.add(edge.to_node_id);
    edgeTargetsBySource.set(edge.from_node_id, seenTargets);

    if (!nodeIds.has(edge.from_node_id) || !nodeIds.has(edge.to_node_id)) {
      addIssue(
        context,
        JOURNEY_ENVELOPE_ERROR_CODES.EdgeEndpointMissing,
        "Edge endpoints must reference nodes in the document",
        ["edges", index],
      );
    }
    if (edge.from_node_id === edge.to_node_id) {
      addIssue(
        context,
        JOURNEY_ENVELOPE_ERROR_CODES.EdgeSelfReference,
        "Self-edges are not allowed",
        ["edges", index],
      );
    }
  }

  if (hasCycle(nodeIds, edges)) {
    addIssue(
      context,
      JOURNEY_ENVELOPE_ERROR_CODES.Cycle,
      "Journey graphs must be acyclic",
      ["edges"],
    );
  }
};

const findEmptyConditionGroupPath = (
  value: unknown,
  path: Array<string | number> = [],
): Array<string | number> | undefined => {
  if (value === null || typeof value !== "object") {
    return undefined;
  }
  const object = value as Record<string, unknown>;
  if (
    (object.type !== "and" && object.type !== "or") ||
    !Array.isArray(object.conditions)
  ) {
    return undefined;
  }
  if (object.conditions.length === 0) {
    return path;
  }
  for (const [index, condition] of object.conditions.entries()) {
    const nestedPath = findEmptyConditionGroupPath(condition, [
      ...path,
      "conditions",
      index,
    ]);
    if (nestedPath) {
      return nestedPath;
    }
  }
  return undefined;
};

export const validateJourneyGraphEnvelopeRules = (
  envelope: TolerantJourneyGraphEnvelope,
  context: JourneyEnvelopeIssueContext,
) => {
  const emptyGroupPath = findEmptyConditionGroupPath(
    envelope.journey.exit_criteria,
  );
  if (emptyGroupPath) {
    addIssue(
      context,
      JOURNEY_ENVELOPE_ERROR_CODES.EmptyExitCriteriaGroup,
      "Exit criteria cannot contain an empty and/or group",
      ["journey", "exit_criteria", ...emptyGroupPath],
    );
  }

  const parsedContextConfig = journeyContextConfigSchema.safeParse(
    envelope.journey.context_config,
  );
  const journeyContextVariableTypes = new Map<
    string,
    JourneyContextVariableType
  >();
  for (const [index, variable] of (parsedContextConfig.success
    ? parsedContextConfig.data.variables
    : []
  ).entries()) {
    if (journeyContextVariableTypes.has(variable.variable_name)) {
      addIssue(
        context,
        JOURNEY_ENVELOPE_ERROR_CODES.DuplicateContextVariable,
        `Context variable ${variable.variable_name} is duplicated`,
        ["journey", "context_config", "variables", index, "variable_name"],
      );
    }
    journeyContextVariableTypes.set(
      variable.variable_name,
      variable.variable_type,
    );
  }
  const contextEnabled = journeyContextVariableTypes.size > 0;

  const couponContextVariableNames = new Set<string>();
  const registerCouponContextVariable = ({
    variableName,
    variableType,
    path,
    namesUsedByThisNode,
  }: {
    variableName: string;
    variableType: JourneyContextVariableType;
    path: Array<string | number>;
    namesUsedByThisNode: Set<string>;
  }) => {
    const declaredType = journeyContextVariableTypes.get(variableName);
    if (contextEnabled && declaredType === undefined) {
      addIssue(
        context,
        JOURNEY_ENVELOPE_ERROR_CODES.MissingCouponContextVariable,
        `Coupon assignment context variable ${variableName} is not declared`,
        path,
      );
    }
    if (
      contextEnabled &&
      declaredType !== undefined &&
      declaredType !== variableType
    ) {
      addIssue(
        context,
        JOURNEY_ENVELOPE_ERROR_CODES.ContextVariableTypeMismatch,
        `Coupon assignment context variable ${variableName} has type ${variableType}, but is declared as ${declaredType}`,
        path,
      );
    }
    // Assignment nodes may share a variable with each other, but one node writes
    // all of its variables in a single insert, so a repeat within a node is a
    // duplicate column.
    if (namesUsedByThisNode.has(variableName)) {
      addIssue(
        context,
        JOURNEY_ENVELOPE_ERROR_CODES.DuplicateCouponContextVariable,
        `Coupon assignment context variable ${variableName} is used more than once on the same node`,
        path,
      );
    }
    namesUsedByThisNode.add(variableName);
    couponContextVariableNames.add(variableName);
  };

  const typedNodes = envelope.nodes.flatMap((candidate, nodeIndex) => {
    const result = journeyEnvelopeNodeSchema.safeParse(candidate);
    return result.success ? [{ node: result.data, nodeIndex }] : [];
  });
  const rawNodeTypesById = new Map<string, string>();
  for (const candidate of envelope.nodes) {
    const nodeId = getNodeId(candidate);
    if (
      nodeId !== undefined &&
      typeof candidate === "object" &&
      candidate !== null &&
      "type" in candidate &&
      typeof candidate.type === "string"
    ) {
      rawNodeTypesById.set(nodeId, candidate.type);
    }
  }

  for (const { node, nodeIndex } of typedNodes) {
    if (
      node.type !== JourneyNodeType.CouponAssignment ||
      node.config.type !== JourneyNodeType.CouponAssignment ||
      node.config.pool_model_id.length === 0
    ) {
      continue;
    }
    const namesUsedByThisNode = new Set<string>();
    registerCouponContextVariable({
      variableName: node.config.assignment_variable_name,
      variableType: "STRING",
      path: ["nodes", nodeIndex, "config", "assignment_variable_name"],
      namesUsedByThisNode,
    });
    for (const [columnIndex, column] of (
      node.config.additional_columns ?? []
    ).entries()) {
      registerCouponContextVariable({
        variableName: column.context_variable_name,
        variableType: column.context_variable_type,
        path: [
          "nodes",
          nodeIndex,
          "config",
          "additional_columns",
          columnIndex,
          "context_variable_name",
        ],
        namesUsedByThisNode,
      });
    }
  }

  const nodesById = new Map<string, JourneyEnvelopeNode>();
  for (const { node, nodeIndex: index } of typedNodes) {
    nodesById.set(node.id, node);

    if (node.type !== node.config.type) {
      addIssue(
        context,
        JOURNEY_ENVELOPE_ERROR_CODES.NodeConfigTypeMismatch,
        `Node type ${node.type} does not match config type ${node.config.type}`,
        ["nodes", index, "config", "type"],
      );
    }

    if (!nodeOwnsSyncs(node) && node.sync_configs !== undefined) {
      addIssue(
        context,
        JOURNEY_ENVELOPE_ERROR_CODES.InvalidSidecarPlacement,
        "Sync sidecars are only allowed on sync nodes and HTTP lookup context nodes",
        ["nodes", index, "sync_configs"],
      );
    }

    if (
      node.type !== JourneyNodeType.Message &&
      node.message_config !== undefined
    ) {
      addIssue(
        context,
        JOURNEY_ENVELOPE_ERROR_CODES.InvalidSidecarPlacement,
        "Message sidecars are only allowed on message nodes",
        ["nodes", index, "message_config"],
      );
    }

    if (node.type === JourneyNodeType.EntryCohort && node.segment_id == null) {
      addIssue(
        context,
        JOURNEY_ENVELOPE_ERROR_CODES.MissingNodeReference,
        "Entry cohort nodes require a segment reference",
        ["nodes", index, "segment_id"],
      );
    }

    if (
      node.type === JourneyNodeType.EntryEvent &&
      node.event_relationship_id == null
    ) {
      addIssue(
        context,
        JOURNEY_ENVELOPE_ERROR_CODES.MissingNodeReference,
        "Entry event nodes require an event relationship reference",
        ["nodes", index, "event_relationship_id"],
      );
    }

    if (
      node.type === JourneyNodeType.WaitUntilEvent &&
      node.config.type === JourneyNodeType.WaitUntilEvent &&
      node.config.event_conditions !== undefined
    ) {
      if (node.segment_id == null) {
        addIssue(
          context,
          JOURNEY_ENVELOPE_ERROR_CODES.MissingNodeReference,
          "Event-based wait nodes require a segment reference",
          ["nodes", index, "segment_id"],
        );
      }
      if (node.event_relationship_id == null) {
        addIssue(
          context,
          JOURNEY_ENVELOPE_ERROR_CODES.MissingNodeReference,
          "Event-based wait nodes require an event relationship reference",
          ["nodes", index, "event_relationship_id"],
        );
      }
    }

    if (node.config.type === JourneyNodeType.TimeDelay) {
      const variants = [
        node.config.delay,
        node.config.propertyDelay,
        node.config.absolute,
        node.config.nextMatchingTime,
      ].filter((value) => value !== undefined);
      if (variants.length !== 1) {
        addIssue(
          context,
          JOURNEY_ENVELOPE_ERROR_CODES.TimeDelayVariantCount,
          "Time-delay nodes must define exactly one delay variant",
          ["nodes", index, "config"],
        );
      }
    }

    if (node.config.type === JourneyNodeType.WaitUntilEvent) {
      const variants = [
        node.config.event_conditions,
        node.config.segment_conditions,
      ].filter((value) => value !== undefined);
      if (variants.length !== 1) {
        addIssue(
          context,
          JOURNEY_ENVELOPE_ERROR_CODES.WaitConditionVariantCount,
          "Wait nodes must define exactly one condition variant",
          ["nodes", index, "config"],
        );
      }
    }

    if (
      node.config.type === JourneyNodeType.EntryEvent &&
      node.config.max_simultaneous_entries === -1 &&
      node.config.max_num_entries !== -1
    ) {
      addIssue(
        context,
        JOURNEY_ENVELOPE_ERROR_CODES.EntryLimitConstraint,
        "Unlimited simultaneous entries require unlimited total entries",
        ["nodes", index, "config", "max_simultaneous_entries"],
      );
    }

    if (node.config.type === JourneyNodeType.Message && node.message_config) {
      if (node.config.message_type !== node.message_config.channel_type) {
        addIssue(
          context,
          JOURNEY_ENVELOPE_ERROR_CODES.MessageChannelMismatch,
          "Message node and sidecar channel types must match",
          ["nodes", index, "message_config", "channel_type"],
        );
      }
    }

    if (nodeOwnsSyncs(node) && node.sync_configs !== undefined) {
      const destinationInstanceIds = new Set<string>();
      const placeholderDestinationIds = new Set<string>();
      for (const [syncIndex, syncConfig] of node.sync_configs.entries()) {
        const [referenceKind, referenceId] =
          "destination_instance_id" in syncConfig
            ? ([
                "destination_instance_id",
                syncConfig.destination_instance_id,
              ] as const)
            : ([
                "placeholder_destination_id",
                syncConfig.placeholder_destination_id,
              ] as const);
        const references =
          referenceKind === "destination_instance_id"
            ? destinationInstanceIds
            : placeholderDestinationIds;
        if (references.has(referenceId)) {
          addIssue(
            context,
            JOURNEY_ENVELOPE_ERROR_CODES.DuplicateSyncReference,
            `Sync reference ${referenceKind}=${referenceId} is duplicated`,
            ["nodes", index, "sync_configs", syncIndex, referenceKind],
          );
        }
        references.add(referenceId);
        if (
          "mode" in syncConfig &&
          (syncConfig.mode === JourneyNodeSyncMode.Lookup) !==
            (node.type === JourneyNodeType.Context)
        ) {
          addIssue(
            context,
            JOURNEY_ENVELOPE_ERROR_CODES.InvalidSyncConfig,
            "Lookup syncs are only allowed on HTTP lookup context nodes",
            ["nodes", index, "sync_configs", syncIndex, "mode"],
          );
        }
      }
    }

    if (
      node.type === JourneyNodeType.Context &&
      node.config.type === JourneyNodeType.Context
    ) {
      if (!contextEnabled) {
        addIssue(
          context,
          JOURNEY_ENVELOPE_ERROR_CODES.ContextNotEnabled,
          "Context nodes require at least one journey context variable",
          ["nodes", index, "config"],
        );
      }
      const variableNames = new Set<string>();
      for (const [valueIndex, value] of node.config.values.entries()) {
        if (variableNames.has(value.variable_name)) {
          addIssue(
            context,
            JOURNEY_ENVELOPE_ERROR_CODES.DuplicateContextVariable,
            `Context variable ${value.variable_name} is duplicated`,
            ["nodes", index, "config", "values", valueIndex, "variable_name"],
          );
        }
        variableNames.add(value.variable_name);
        const declaredType = journeyContextVariableTypes.get(
          value.variable_name,
        );
        if (contextEnabled && declaredType === undefined) {
          addIssue(
            context,
            JOURNEY_ENVELOPE_ERROR_CODES.MissingContextVariable,
            `Context variable ${value.variable_name} is not declared`,
            ["nodes", index, "config", "values", valueIndex, "variable_name"],
          );
        }
        if (
          contextEnabled &&
          declaredType !== undefined &&
          declaredType !== value.variable_type
        ) {
          addIssue(
            context,
            JOURNEY_ENVELOPE_ERROR_CODES.ContextVariableTypeMismatch,
            `Context variable ${value.variable_name} has type ${value.variable_type}, but is declared as ${declaredType}`,
            ["nodes", index, "config", "values", valueIndex, "variable_type"],
          );
        }
        if (couponContextVariableNames.has(value.variable_name)) {
          addIssue(
            context,
            JOURNEY_ENVELOPE_ERROR_CODES.CouponContextVariableOwnership,
            `Context variable ${value.variable_name} is owned by a coupon assignment node`,
            ["nodes", index, "config", "values", valueIndex, "variable_name"],
          );
        }
      }
    }

    if (
      node.type === JourneyNodeType.CouponAssignment &&
      node.config.type === JourneyNodeType.CouponAssignment &&
      node.config.pool_model_id.length > 0 &&
      !contextEnabled
    ) {
      addIssue(
        context,
        JOURNEY_ENVELOPE_ERROR_CODES.ContextNotEnabled,
        "Configured coupon assignment nodes require at least one journey context variable",
        ["nodes", index, "config"],
      );
    }
  }

  validateJourneyGraphEnvelopeTopologyRules(envelope, context);

  const entryCount = envelope.nodes.filter(
    (node) =>
      typeof node === "object" &&
      node !== null &&
      "type" in node &&
      (node.type === JourneyNodeType.EntryCohort ||
        node.type === JourneyNodeType.EntryEvent),
  ).length;
  if (entryCount !== 1) {
    addIssue(
      context,
      JOURNEY_ENVELOPE_ERROR_CODES.EntryNodeCount,
      `Expected exactly one entry node, found ${entryCount}`,
      ["nodes"],
    );
  }

  const hasEventEntry = envelope.nodes.some(
    (node) =>
      typeof node === "object" &&
      node !== null &&
      "type" in node &&
      node.type === JourneyNodeType.EntryEvent,
  );
  const hasCouponAssignment = envelope.nodes.some(
    (node) =>
      typeof node === "object" &&
      node !== null &&
      "type" in node &&
      node.type === JourneyNodeType.CouponAssignment,
  );
  if (hasEventEntry && hasCouponAssignment) {
    addIssue(
      context,
      JOURNEY_ENVELOPE_ERROR_CODES.CouponAssignmentEventEntry,
      "Coupon assignment nodes cannot be used with event-based entry",
      ["nodes"],
    );
  }

  const noteIds = new Set<string>();
  const notes = Array.isArray(envelope.journey.notes)
    ? envelope.journey.notes.flatMap((candidate, noteIndex) => {
        const result = journeyNoteSchema.safeParse(candidate);
        return result.success ? [{ note: result.data, noteIndex }] : [];
      })
    : [];
  const allNodeIds = new Set(
    envelope.nodes.flatMap((node) => {
      const nodeId = getNodeId(node);
      return nodeId === undefined ? [] : [nodeId];
    }),
  );
  for (const { note, noteIndex } of notes) {
    if (note.id !== undefined) {
      if (noteIds.has(note.id)) {
        addIssue(
          context,
          JOURNEY_ENVELOPE_ERROR_CODES.DuplicateNoteId,
          `Note ID ${note.id} is duplicated`,
          ["journey", "notes", noteIndex, "id"],
        );
      }
      noteIds.add(note.id);
    }
    if (
      note.node_id !== null &&
      note.node_id !== undefined &&
      !allNodeIds.has(note.node_id)
    ) {
      addIssue(
        context,
        JOURNEY_ENVELOPE_ERROR_CODES.NoteNodeMissing,
        `Note references missing node ${note.node_id}`,
        ["journey", "notes", noteIndex, "node_id"],
      );
    }
  }

  const incomingParentIdsByNodeId = new Map<string, Set<string>>();
  const outgoingByNodeId = new Map<string, JourneyEnvelopeNode[]>();
  const typedEdges = envelope.edges.flatMap((candidate, edgeIndex) => {
    const edge = getEdge(candidate);
    return edge === undefined ? [] : [{ edge, edgeIndex }];
  });
  for (const { edge } of typedEdges) {
    const incomingParentIds =
      incomingParentIdsByNodeId.get(edge.to_node_id) ?? new Set<string>();
    incomingParentIds.add(edge.from_node_id);
    incomingParentIdsByNodeId.set(edge.to_node_id, incomingParentIds);

    const child = nodesById.get(edge.to_node_id);
    if (child) {
      outgoingByNodeId.set(edge.from_node_id, [
        ...(outgoingByNodeId.get(edge.from_node_id) ?? []),
        child,
      ]);
    }
  }

  const parentFamilies = new Map<JourneyNodeType, JourneyNodeType>(
    [...FAN_OUT_NODE_TYPES].flatMap(([parent, branch]) =>
      branch === null ? [] : [[branch, parent] as const],
    ),
  );

  for (const { node, nodeIndex: index } of typedNodes) {
    const children = outgoingByNodeId.get(node.id) ?? [];
    const expectedParent = parentFamilies.get(node.type);
    const incomingParentCount =
      incomingParentIdsByNodeId.get(node.id)?.size ?? 0;
    if (expectedParent && incomingParentCount !== 1) {
      addIssue(
        context,
        JOURNEY_ENVELOPE_ERROR_CODES.BranchParentCardinality,
        `${node.type} nodes require exactly one parent, found ${incomingParentCount}`,
        ["nodes", index],
      );
    }

    if (!FAN_OUT_NODE_TYPES.has(node.type) && children.length > 1) {
      addIssue(
        context,
        JOURNEY_ENVELOPE_ERROR_CODES.InvalidFanOut,
        `Node ${node.id} cannot have more than one child`,
        ["nodes", index],
      );
    }

    if (node.type === JourneyNodeType.Segments) {
      if (children.length === 0) {
        addIssue(
          context,
          JOURNEY_ENVELOPE_ERROR_CODES.SegmentsBranchCardinality,
          "Segments nodes require at least one segment branch",
          ["nodes", index],
        );
      }
      const branchConfigs = children
        .filter((child) => child.config.type === JourneyNodeType.SegmentBranch)
        .map((child) => child.config as SegmentBranchConfig);
      const catchAllBranches = branchConfigs.filter(
        (config) => config.segment_is_catch_all,
      );
      const priorityRanks = new Set<number>();
      const duplicatePriority = branchConfigs.find((config) => {
        if (priorityRanks.has(config.segment_priority_rank)) {
          return true;
        }
        priorityRanks.add(config.segment_priority_rank);
        return false;
      });
      if (duplicatePriority) {
        addIssue(
          context,
          JOURNEY_ENVELOPE_ERROR_CODES.DuplicateSegmentPriority,
          `Segment branch priority ${duplicatePriority.segment_priority_rank} is duplicated`,
          ["nodes", index],
        );
      }
      const maxPriority = Math.max(
        ...branchConfigs.map((config) => config.segment_priority_rank),
      );
      if (
        catchAllBranches.length !== 1 ||
        catchAllBranches.some(
          (config) => config.segment_priority_rank !== maxPriority,
        )
      ) {
        addIssue(
          context,
          JOURNEY_ENVELOPE_ERROR_CODES.SegmentCatchAllOrder,
          "Segments nodes require exactly one catch-all branch, last in priority order",
          ["nodes", index],
        );
      }
    }

    if (node.config.type === JourneyNodeType.Decision) {
      // A duplicate edge is reported but not dropped, so dedupe here.
      const armCount = new Set(children.map((child) => child.id)).size;
      if (armCount < 2) {
        addIssue(
          context,
          JOURNEY_ENVELOPE_ERROR_CODES.DecisionArmCardinality,
          `Decision nodes require at least two arms, found ${armCount}`,
          ["nodes", index],
        );
      }
    }

    if (node.type === JourneyNodeType.Splits) {
      const percentageTotal = children.reduce(
        (total, child) =>
          child.config.type === JourneyNodeType.SplitBranch
            ? total + child.config.percentage
            : total,
        0,
      );
      if (percentageTotal !== 100) {
        addIssue(
          context,
          JOURNEY_ENVELOPE_ERROR_CODES.SplitPercentageTotal,
          `Split branch percentages must total 100, found ${percentageTotal}`,
          ["nodes", index],
        );
      }
    }

    if (node.type === JourneyNodeType.WaitUntilEvent) {
      const waitChildren = children.filter(
        (child) => child.config.type === JourneyNodeType.WaitUntilEventBranch,
      );
      const eventCount = waitChildren.filter(
        (child) =>
          child.config.type === JourneyNodeType.WaitUntilEventBranch &&
          child.config.branch === "event",
      ).length;
      const timeoutCount = waitChildren.filter(
        (child) =>
          child.config.type === JourneyNodeType.WaitUntilEventBranch &&
          child.config.branch === "timeout",
      ).length;
      if (eventCount !== 1 || timeoutCount !== 1) {
        addIssue(
          context,
          JOURNEY_ENVELOPE_ERROR_CODES.WaitBranchCardinality,
          "Wait nodes require exactly one event and one timeout branch",
          ["nodes", index],
        );
      }
    }
  }

  for (const { edge, edgeIndex } of typedEdges) {
    const parentType =
      nodesById.get(edge.from_node_id)?.type ??
      rawNodeTypesById.get(edge.from_node_id);
    const child = nodesById.get(edge.to_node_id);
    if (parentType === undefined || !child) {
      continue;
    }
    const expectedParent = parentFamilies.get(child.type);
    if (expectedParent && parentType !== expectedParent) {
      addIssue(
        context,
        JOURNEY_ENVELOPE_ERROR_CODES.BranchFamilyMismatch,
        `${child.type} nodes must be direct children of ${expectedParent}`,
        ["edges", edgeIndex],
      );
    }
  }
};
